import { useState } from "react";
import { useFormik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import {
  createLoginCredentialsSchema,
  createOTPSchema,
} from "@/schemas/loginSchema";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { HelpCircle, Mail } from "lucide-react";

import authService from "@/services/authService";
import { canAccessDashboard } from "@/utils/subscription-status";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSeparator,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import { useTheme } from "@/hooks/useTheme";
import { useAppDispatch } from "@/hooks/redux";
import { checkAuthStatus } from "@/store/slices/authSlice";

// DEV NOTE: OTP toast for debugging is included below. REMOVE BEFORE PRODUCTION DEPLOYMENT.

// Step type for the authentication flow
type AuthStep = "credentials" | "otp";

export function LoginForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [authStep, setAuthStep] = useState<AuthStep>("credentials");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [requiresEmailVerification, setRequiresEmailVerification] =
    useState(false);
  const [isContactSupportOpen, setIsContactSupportOpen] = useState(false);
  const { isDark } = useTheme();
  // Helper function to open email client
  const handleContactSupport = () => {
    const email = import.meta.env.VITE_SUPPORT_EMAIL;
    const subject = encodeURIComponent(
      `Demande de Support - ${import.meta.env.VITE_APP_NAME}`,
    );
    const currentDate = new Date().toLocaleDateString("fr-FR");
    const currentTime = new Date().toLocaleTimeString("fr-FR");

    const body = encodeURIComponent(
      `Bonjour l'équipe Support,

J'ai besoin d'assistance concernant :
[Veuillez décrire votre problème ou question ici]


TYPE DE PROBLÈME :
□ Problème de connexion / authentification
                    □ Problème technique
□ Problème de facturation / abonnement
□ Problème technique (bugs, erreurs)
□ Question sur l'utilisation
□ Demande de fonctionnalité
□ Autre : _______________


DÉTAILS TECHNIQUES :
- Navigateur utilisé : ${
        navigator.userAgent.includes("Chrome")
          ? "Chrome"
          : navigator.userAgent.includes("Firefox")
            ? "Firefox"
            : navigator.userAgent.includes("Safari")
              ? "Safari"
              : navigator.userAgent.includes("Edge")
                ? "Edge"
                : "Autre"
      }
- Système d'exploitation : ${navigator.platform || "Non déterminé"}
- Date et heure : ${currentDate} à ${currentTime}
- URL de la page concernée : ${window.location.href}


INFORMATIONS UTILISATEUR :
- Email : Non renseigné (page de connexion)
- Nom complet : Non renseigné (page de connexion)
- Statut : Tentative de connexion


ÉTAPES POUR REPRODUIRE LE PROBLÈME :
1. 
2. 
3. 

RÉSULTAT ATTENDU :


RÉSULTAT OBTENU :


Merci pour votre aide et votre support.

Cordialement`,
    );

    // Create mailto link
    const mailtoLink = `mailto:${email}?subject=${subject}&body=${body}`;

    // Open email client
    window.location.href = mailtoLink;
  };

  // Credentials form validation
  const credentialsForm = useFormik({
    initialValues: {
      email: "",
      password: "",
    },
    validationSchema: createLoginCredentialsSchema(t),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      try {
        // Verify credentials and request OTP
        const result = await authService.verifyCredentialsAndRequestOTP({
          email: values.email,
          password: values.password,
        });
        if (result.success) {
          setUserId(result.userId); // Store the userId for OTP verification
          setRequiresEmailVerification(
            result.requiresEmailVerification || false,
          );
          setAuthStep("otp");
          // DEV ONLY: Show OTP in toast for debugging. Remove in production.
          if (result.otp && process.env.NODE_ENV === "development") {
            toast.info(`Code OTP (dev): ${result.otp}`);
          }
        }
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.invalidCredentials"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  // OTP form validation
  const otpForm = useFormik({
    initialValues: {
      otp: "",
    },
    validationSchema: createOTPSchema(t),
    onSubmit: async (values) => {
      setIsLoading(true);
      setError(null);
      try {
        // Use the userId stored from the credentials step
        if (!userId) {
          throw new Error(t("auth.userIdMissing"));
        }

        // Use different verification method based on whether email verification is required
        const response = requiresEmailVerification
          ? await authService.verifyRegistrationOTP({
              userId: userId,
              otpCode: values.otp,
            })
          : await authService.verifyLoginOTPAndSignIn({
              userId: userId,
              otpCode: values.otp,
            });

        // ✅ Récupérer les données utilisateur complètes (y compris enabledFeatures) depuis Redux
        // Cela garantit que les features sont correctement mises à jour après la connexion
        await dispatch(checkAuthStatus());

        // Redirect based on subscription access
        // Active ou Trialing (non annulé) → dashboard
        // Sinon → plans
        const hasAccess = canAccessDashboard(response.user);
        navigate(hasAccess ? "/dashboard" : "/plans");
      } catch (error) {
        if (error instanceof Error) {
          setError(error.message);
        } else {
          setError(t("auth.otpVerificationError"));
        }
      } finally {
        setIsLoading(false);
      }
    },
  });

  // Reset authentication flow
  const handleResetFlow = () => {
    setAuthStep("credentials");
    setUserId(null);
    setRequiresEmailVerification(false);
    otpForm.resetForm();
    setError(null);
  };

  return (
    <Card className="w-full">
      <CardHeader className="space-y-1">
        <Link to="/" className="flex justify-center gap-2 mb-4">
          <img
            src={
              isDark
                ? "/assets/logo_full_dark.webp"
                : "/assets/logo_full_light.webp"
            }
            alt="logo"
            className="h-14 transition-all duration-300 "
          />
        </Link>
        <CardTitle className="text-2xl text-center">
          {authStep === "credentials"
            ? t("auth.signIn")
            : requiresEmailVerification
              ? t("auth.verifyEmailTitle")
              : t("auth.enterOtpTitle")}
        </CardTitle>
        <CardDescription className="text-center">
          {authStep === "credentials"
            ? t("auth.credentialsPrompt")
            : requiresEmailVerification
              ? t("auth.verificationOtpSentTo", {
                  email: credentialsForm.values.email,
                })
              : t("auth.otpSentTo", { email: credentialsForm.values.email })}
        </CardDescription>
      </CardHeader>
      <CardContent className="grid gap-4">
        {error && (
          <div className="p-3 text-sm text-destructive bg-destructive/10 rounded-md">
            {error}
          </div>
        )}

        {authStep === "credentials" ? (
          <form onSubmit={credentialsForm.handleSubmit}>
            <div className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="email">{t("auth.email")}</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t("auth.placeholder.email")}
                  value={credentialsForm.values.email}
                  onChange={credentialsForm.handleChange}
                  onBlur={credentialsForm.handleBlur}
                  disabled={isLoading}
                  maxLength={100}
                />
                {credentialsForm.touched.email &&
                  credentialsForm.errors.email && (
                    <p className="text-sm text-destructive">
                      {credentialsForm.errors.email}
                    </p>
                  )}
              </div>

              <div className="grid gap-2">
                <Label htmlFor="password">{t("auth.password")}</Label>
                <Input
                  id="password"
                  name="password"
                  type="password"
                  value={credentialsForm.values.password}
                  onChange={credentialsForm.handleChange}
                  onBlur={credentialsForm.handleBlur}
                  disabled={isLoading}
                  maxLength={128}
                />
                {credentialsForm.touched.password &&
                  credentialsForm.errors.password && (
                    <p className="text-sm text-destructive">
                      {credentialsForm.errors.password}
                    </p>
                  )}
              </div>

              <div className="flex justify-end">
                <a
                  href="/forgot-password"
                  className="text-sm text-muted-foreground hover:text-primary"
                >
                  {t("auth.forgotPassword")}
                </a>
              </div>
              <Button
                className="w-full mt-2"
                type="submit"
                disabled={isLoading}
              >
                {isLoading ? t("auth.verifying") : t("auth.signIn")}
              </Button>
            </div>
          </form>
        ) : (
          <form onSubmit={otpForm.handleSubmit}>
            <div className="grid gap-2">
              <Label htmlFor="otp">{t("auth.verificationCode")}</Label>
              <div className="flex justify-center">
                <InputOTP
                  maxLength={6}
                  value={otpForm.values.otp}
                  onChange={(value: string) =>
                    otpForm.setFieldValue("otp", value)
                  }
                  disabled={isLoading}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                  </InputOTPGroup>
                  <InputOTPSeparator />
                  <InputOTPGroup>
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
              </div>
              {otpForm.touched.otp && otpForm.errors.otp && (
                <p className="text-sm text-destructive">{otpForm.errors.otp}</p>
              )}
              <Button
                className="w-full mt-4"
                type="submit"
                disabled={
                  isLoading ||
                  !otpForm.values.otp ||
                  otpForm.values.otp.length < 6
                }
              >
                {isLoading
                  ? t("auth.verifying")
                  : requiresEmailVerification
                    ? t("auth.verifyEmail")
                    : t("auth.verifyAndSignIn")}
              </Button>
              <Button
                variant="ghost"
                className="mt-2"
                type="button"
                onClick={handleResetFlow}
                disabled={isLoading}
              >
                {t("auth.backToSignIn")}
              </Button>
            </div>
          </form>
        )}
      </CardContent>
      <CardFooter className="flex flex-col">
        {/* Contact Support Dialog */}
        <Dialog
          open={isContactSupportOpen}
          onOpenChange={setIsContactSupportOpen}
        >
          <DialogTrigger asChild>
            <Button variant="ghost" size="sm" className="mb-3 gap-2">
              <HelpCircle className="h-4 w-4" />
              {t("dashboard.contactSupport")}
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <HelpCircle className="h-5 w-5" />
                {t("dashboard.contactSupport")}
              </DialogTitle>
              <DialogDescription>
                {t("dashboard.contactSupportDescription")}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="flex flex-col space-y-3">
                <div className="flex items-center justify-between p-4 border rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <Mail className="h-5 w-5 text-muted-foreground" />
                    <div>
                      <p className="font-medium">Email Support</p>
                      <p className="text-sm text-muted-foreground">
                        {import.meta.env.VITE_SUPPORT_EMAIL}
                      </p>
                    </div>
                  </div>
                </div>

                <Button onClick={handleContactSupport} className="w-full">
                  <Mail className="h-4 w-4 mr-2" />
                  {t("dashboard.openEmailApp")}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  {t("dashboard.emailAppNote")}
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        <div className="mt-2 text-center text-sm">
          {t("auth.dontHaveAccount")}{" "}
          <a href="/register" className="underline text-primary">
            {t("auth.signUp")}
          </a>
        </div>
      </CardFooter>
    </Card>
  );
}
